import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Sliders, Grid3x3, Zap, Link2 } from 'lucide-react';
import { Knob } from './Knob';
import { WaveformStrip } from './WaveformStrip';
import { CuePad } from './CuePad';
import { fmtTs } from './fmt';

/**
 * A single deck (A or B). Owns nothing — every knob / cue / loop is a
 * controlled input driven by the parent Decks page. Preserves every
 * data-testid used by testing_agent and previous iterations.
 */
export const DeckPanel = ({
  label, deck, tempo, setTempo,
  low, setLow, mid, setMid, high, setHigh, filter, setFilter, volume,
  side, cues, loops, activeLoop, onCue, onLoop,
  onVocalKill, vocalKilled, vocalBusy, demucsEnabled,
}) => {
  // Re-apply state every render so first Play immediately reflects current knob positions
  useEffect(() => { deck.setRate(tempo); }, [tempo, deck, deck.isPlaying]);
  useEffect(() => { deck.setLow(low); }, [low, deck, deck.isPlaying]);
  useEffect(() => { deck.setMid(mid); }, [mid, deck, deck.isPlaying]);
  useEffect(() => { deck.setHigh(high); }, [high, deck, deck.isPlaying]);
  useEffect(() => { deck.setFilterFreq(filter); }, [filter, deck, deck.isPlaying]);
  useEffect(() => { deck.setGain(volume); }, [volume, deck, deck.isPlaying]);

  const [showEqPanel, setShowEqPanel] = useState(true);
  const [showCuePanel, setShowCuePanel] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    const el = deck.audioEl?.current;
    if (!el) return;
    const onT = () => { setElapsed(el.currentTime || 0); setTotal(el.duration || 0); };
    el.addEventListener('timeupdate', onT);
    el.addEventListener('loadedmetadata', onT);
    return () => { el.removeEventListener('timeupdate', onT); el.removeEventListener('loadedmetadata', onT); };
  }, [deck.audioEl, deck.trackTitle]);

  const displayBpm = (deck.trackBpm ? deck.trackBpm * tempo : tempo * 128).toFixed(1);
  const trackTitle = deck.trackTitle || 'No track loaded';

  return (
    <div
      className="relative rounded-2xl p-4 md:p-5 flex-1 overflow-hidden"
      data-testid={`deck-${side}`}
      style={{
        background: 'radial-gradient(ellipse at top, rgba(255,176,0,0.06), transparent 60%), linear-gradient(180deg, #0d0d0d 0%, #050505 100%)',
        boxShadow: 'inset 0 1px 0 rgba(255,176,0,0.15), inset 0 -1px 0 rgba(0,0,0,0.5), 0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,176,0,0.2)',
      }}
    >
      {/* Header — track title / bpm / kill vox */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] font-black text-[#FFB000]/70 mb-1">{label}</div>
          <div className="text-white font-heading font-black text-base md:text-lg truncate leading-tight" data-testid={`deck-${side}-title`} title={trackTitle}>
            {trackTitle}
          </div>
          <div className="text-[#A1A1AA] font-mono text-[10px] tracking-widest mt-0.5">
            {fmtTs(elapsed)} <span className="opacity-40">/ {fmtTs(total)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-[#FFB000]/60 font-bold leading-none">BPM</div>
            <div className="text-white font-mono font-black text-2xl leading-tight tabular-nums" data-testid={`deck-${side}-bpm`}>{displayBpm}</div>
            {deck.trackKey && (
              <div className="text-[10px] font-mono font-bold text-[#FFB000] tracking-widest mt-0.5">KEY {deck.trackKey}</div>
            )}
          </div>
          <button
            onClick={onVocalKill}
            disabled={!demucsEnabled || vocalBusy}
            title={demucsEnabled ? (vocalKilled ? 'Restore vocals' : 'Kill vocals — sing over the instrumental') : 'Requires DEMUCS_WORKER_URL'}
            className={`text-[9px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              !demucsEnabled ? 'border-white/10 text-[#A1A1AA]'
              : vocalKilled ? 'border-[#EF4444] bg-[#EF4444]/10 text-[#EF4444]'
              : 'border-[#FFB000]/40 text-[#FFB000] hover:bg-[#FFB000]/10'
            }`}
            data-testid={`deck-${side}-vocal-kill`}
          >
            {vocalBusy ? '…' : (vocalKilled ? 'VOX OFF' : 'KILL VOX')}
          </button>
        </div>
      </div>

      {/* Waveform strip */}
      <div className="rounded-lg bg-black/60 border border-[#FFB000]/15 mb-4 overflow-hidden">
        <WaveformStrip audioRef={deck.audioEl} isPlaying={deck.isPlaying} seed={trackTitle} testid={`deck-${side}-waveform`} />
      </div>

      {/* Main platter row: side rail + jog wheel */}
      <div className="flex items-stretch gap-3 mb-3">
        {/* Side rail — vertical pill buttons */}
        <div className={`flex flex-col gap-1.5 flex-shrink-0 ${side === 'b' ? 'order-2' : ''}`}>
          <button
            onClick={() => setShowEqPanel(v => !v)}
            className={`w-10 h-10 rounded-lg border font-black text-[9px] uppercase tracking-widest transition-colors flex items-center justify-center ${showEqPanel ? 'bg-[#FFB000]/15 border-[#FFB000] text-[#FFB000]' : 'bg-black/40 border-white/10 text-[#A1A1AA] hover:text-[#FFB000] hover:border-[#FFB000]/40'}`}
            title="EQ + filters"
            data-testid={`deck-${side}-rail-eq`}
          >
            <Sliders size={14} />
          </button>
          <button
            onClick={onVocalKill}
            disabled={!demucsEnabled || vocalBusy}
            className={`w-10 h-10 rounded-lg border font-black text-[9px] uppercase tracking-widest transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${vocalKilled ? 'bg-[#EF4444]/20 border-[#EF4444] text-[#EF4444]' : 'bg-black/40 border-white/10 text-[#A1A1AA] hover:text-[#FFB000] hover:border-[#FFB000]/40'}`}
            title="Kill vocals"
            data-testid={`deck-${side}-rail-vox`}
          >
            PRO
          </button>
          <button
            onClick={() => setShowCuePanel(v => !v)}
            className={`w-10 h-10 rounded-lg border font-black text-[9px] uppercase tracking-widest transition-colors flex items-center justify-center ${showCuePanel ? 'bg-[#FFB000]/15 border-[#FFB000] text-[#FFB000]' : 'bg-black/40 border-white/10 text-[#A1A1AA] hover:text-[#FFB000] hover:border-[#FFB000]/40'}`}
            title="Cues + loops"
            data-testid={`deck-${side}-rail-cues`}
          >
            <Grid3x3 size={14} />
          </button>
          <button
            className="w-10 h-10 rounded-lg border bg-black/40 border-white/10 text-[#A1A1AA] hover:text-[#FFB000] hover:border-[#FFB000]/40 transition-colors flex items-center justify-center"
            title="FX (coming soon)"
            data-testid={`deck-${side}-rail-fx`}
          >
            <Zap size={14} />
          </button>
        </div>

        {/* Jog wheel */}
        <div className="relative flex-1 aspect-square max-w-[280px] mx-auto">
          {/* Hard gold rim ring */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'conic-gradient(from 220deg, #C68600 0deg, #FFE28C 40deg, #FFB000 100deg, #7a4d00 180deg, #FFB000 260deg, #FFE28C 320deg, #C68600 360deg)',
              boxShadow: '0 0 30px rgba(255,176,0,0.35), 0 6px 12px rgba(0,0,0,0.8)',
            }}
          />
          {/* Specular highlight */}
          <div className="absolute inset-0 rounded-full pointer-events-none" style={{
            background: 'radial-gradient(ellipse at 30% 15%, rgba(255,255,255,0.5), transparent 25%)',
          }} />
          {/* Inner platter */}
          <motion.div
            className="absolute inset-[8px] rounded-full overflow-hidden"
            animate={{ rotate: deck.isPlaying ? 360 : 0 }}
            transition={{ duration: 2 / (tempo || 1), repeat: deck.isPlaying ? Infinity : 0, ease: 'linear' }}
            style={{
              background: 'radial-gradient(circle at 45% 40%, #1a1a1a 0%, #0a0a0a 55%, #000 100%)',
              boxShadow: 'inset 0 0 40px rgba(0,0,0,0.95), inset 0 2px 4px rgba(255,255,255,0.06)',
            }}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full border border-[#FFB000]"
                style={{ inset: `${5 + i * 4}%`, borderWidth: '1px', opacity: 0.08 + i * 0.008 }}
              />
            ))}
            <div className="absolute inset-0 rounded-full pointer-events-none" style={{
              background: 'repeating-radial-gradient(circle at center, rgba(255,176,0,0.09) 0 1px, transparent 1px 3px)',
              maskImage: 'radial-gradient(circle at center, black 25%, transparent 62%)',
              WebkitMaskImage: 'radial-gradient(circle at center, black 25%, transparent 62%)',
            }} />
            <div className="absolute inset-0 flex items-start justify-center pt-[8%]">
              <div className="w-[3px] h-[35%] rounded-full bg-[#FFB000] shadow-[0_0_10px_rgba(255,176,0,0.9)]" />
            </div>
            <div className="absolute inset-[36%] rounded-full flex items-center justify-center" style={{
              background: 'radial-gradient(circle at 35% 30%, #FFF3C4 0%, #FFD066 30%, #FFB000 60%, #7a4d00 95%)',
              boxShadow: 'inset 0 2px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.6), 0 0 14px rgba(255,176,0,0.55)',
            }}>
              <div className="absolute inset-[10%] rounded-full border border-black/60" />
              {[0, 90, 180, 270].map(a => (
                <div key={a} className="absolute w-1.5 h-1.5 rounded-full bg-black/70"
                  style={{ transform: `rotate(${a}deg) translateY(-40%)`, boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.3)' }} />
              ))}
              <div className="w-2 h-2 rounded-full bg-black shadow-[inset_0_1px_2px_rgba(255,255,255,0.2)]" />
            </div>
          </motion.div>
          {/* Click-to-play overlay */}
          <button
            onClick={deck.toggle}
            className="absolute inset-0 rounded-full flex items-center justify-center opacity-30 hover:opacity-100 transition-opacity"
            data-testid={`deck-${side}-play`}
            aria-label={deck.isPlaying ? 'Pause' : 'Play'}
          >
            <div className="w-16 h-16 rounded-full flex items-center justify-center pointer-events-none" style={{
              background: 'radial-gradient(circle at 30% 30%, #FFE28C, #FFB000 60%, #7a4d00)',
              boxShadow: '0 0 24px rgba(255,176,0,0.7)',
            }}>
              {deck.isPlaying ? <Pause size={26} fill="black" /> : <Play size={26} fill="black" className="ml-1" />}
            </div>
          </button>
        </div>
      </div>

      {/* EQ / filter / tempo — 5 rotary knobs */}
      {showEqPanel && (
        <div className="grid grid-cols-5 gap-1.5 mb-3 py-2 rounded-lg bg-black/40 border border-[#FFB000]/10 px-2">
          <Knob label="Low" value={low} min={-24} max={12} step={0.1} onChange={setLow} testid={`deck-${side}-low`} format={v => `${v > 0 ? '+' : ''}${v.toFixed(1)}`} />
          <Knob label="Mid" value={mid} min={-24} max={12} step={0.1} onChange={setMid} testid={`deck-${side}-mid`} format={v => `${v > 0 ? '+' : ''}${v.toFixed(1)}`} />
          <Knob label="High" value={high} min={-24} max={12} step={0.1} onChange={setHigh} testid={`deck-${side}-high`} format={v => `${v > 0 ? '+' : ''}${v.toFixed(1)}`} />
          <Knob label="Filter" value={filter} min={80} max={20000} step={10} onChange={setFilter} testid={`deck-${side}-filter`} format={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : `${v|0}`} />
          <Knob label="Tempo" value={tempo} min={0.5} max={1.5} step={0.01} onChange={setTempo} testid={`deck-${side}-tempo`} format={v => `${v > 1 ? '+' : ''}${((v - 1) * 100).toFixed(1)}%`} />
        </div>
      )}

      {/* Big play button + tempo nudger */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center bg-black/60 border border-[#FFB000]/25 rounded-full px-1 h-11 flex-shrink-0">
          <button
            onClick={() => setTempo(Math.max(0.5, +(tempo - 0.01).toFixed(3)))}
            className="w-8 h-8 rounded-full text-[#FFB000] hover:bg-[#FFB000]/10 transition-colors font-black text-lg leading-none"
            data-testid={`deck-${side}-tempo-down`}
            title="Slower"
          >−</button>
          <div className="px-2 flex items-center gap-1 text-[#FFB000] font-mono font-bold text-xs">
            <Link2 size={11} />
            <span className="tabular-nums">{((tempo - 1) * 100).toFixed(1)}%</span>
          </div>
          <button
            onClick={() => setTempo(Math.min(1.5, +(tempo + 0.01).toFixed(3)))}
            className="w-8 h-8 rounded-full text-[#FFB000] hover:bg-[#FFB000]/10 transition-colors font-black text-lg leading-none"
            data-testid={`deck-${side}-tempo-up`}
            title="Faster"
          >+</button>
        </div>
        <button
          onClick={deck.toggle}
          disabled={!deck.trackTitle}
          className="flex-1 h-11 rounded-full flex items-center justify-center gap-2 font-heading font-black text-sm text-black disabled:opacity-40 disabled:cursor-not-allowed transition-transform active:scale-[0.98]"
          data-testid={`deck-${side}-play-btn`}
          style={{
            background: deck.trackTitle ? 'linear-gradient(180deg, #FFD066 0%, #FFB000 55%, #C68600 100%)' : 'linear-gradient(180deg, #333 0%, #1a1a1a 100%)',
            boxShadow: deck.trackTitle ? '0 4px 12px rgba(255,176,0,0.4), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 0 rgba(0,0,0,0.2)' : 'inset 0 1px 0 rgba(255,255,255,0.05)',
          }}
        >
          {deck.isPlaying ? <><Pause size={16} fill="black" /> PAUSE</> : <><Play size={16} fill="black" className="ml-0.5"/> PLAY</>}
        </button>
      </div>

      {/* Hot cues (8) + loops (4) — collapsible */}
      {showCuePanel && (
        <>
          <div className="mb-3">
            <div className="text-[9px] uppercase tracking-widest text-[#FFB000]/60 font-black mb-1.5 flex items-center justify-between">
              <span>Hot cues</span>
              <span className="opacity-50 text-[#A1A1AA] normal-case tracking-wide">Right-click to clear</span>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {cues.map((c, i) => (
                <CuePad
                  key={i}
                  filled={!!c}
                  active={false}
                  label={c ? fmtTs(c.position_sec) : `${i + 1}`}
                  hint={c ? `Jump to ${fmtTs(c.position_sec)}` : 'Save current position'}
                  onClick={(e, right = false) => onCue(i, right || (e && e.shiftKey))}
                  testid={`deck-${side}-cue-${i}`}
                />
              ))}
            </div>
          </div>

          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#FFB000]/60 font-black mb-1.5 flex items-center justify-between">
              <span>Saved loops</span>
              <span className="opacity-50 text-[#A1A1AA] normal-case tracking-wide">In → Out → Play</span>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {loops.map((l, i) => {
                let text = `L${i + 1}`;
                if (l && l.end_sec != null) text = `${fmtTs(l.start_sec)}→${fmtTs(l.end_sec)}`;
                else if (l) text = `IN ${fmtTs(l.start_sec)}`;
                return (
                  <CuePad
                    key={i}
                    filled={!!l}
                    active={activeLoop === i}
                    label={text}
                    hint={l ? (activeLoop === i ? 'Looping — click to release' : 'Play this loop') : 'Set loop IN'}
                    onClick={(e, right = false) => onLoop(i, right || (e && e.shiftKey))}
                    testid={`deck-${side}-loop-${i}`}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
